/**
 * Phase F11 FE-2b1 — periodic webcam-still capture loop.
 *
 * Engaged ONLY while the assessment is `in_progress` AND the proctor
 * profile is active. Maintains exactly one MediaStream for the
 * lifetime of the attempt (no per-tick reopening — that would burn
 * permission UI flickers on some browsers and double the GPU pipeline
 * cost for a 1 fps capture cadence).
 *
 * Tick cadence:
 *   Default 30 s. Configurable via the `intervalMs` arg so future F12
 *   ML signals can lift the cadence (e.g. "we just saw five
 *   tab_blurred events in 60s — bump to 5s stills until the focus
 *   stabilises"). Minimum 5 s; anything tighter risks the bucket
 *   rate-limiting the BE's signed-URL minting.
 *
 * Failure semantics:
 *   * getUserMedia denied / hardware unavailable → record a
 *     `consent_denied` event with `{ reason: "webcam_loop_*" }` so the
 *     F12 review surface can distinguish "applicant revoked mid-test"
 *     from the initial consent gate's denials. Stop ticking.
 *   * Per-tick capture or upload failure → swallow + report via
 *     `onTransportError` (already wired through the proctor client).
 *     The next tick still fires; we don't kill the entire loop on a
 *     single network hiccup.
 *
 * Privacy: the loop never renders the live stream; bytes are captured
 * to an offscreen canvas and uploaded directly. The applicant's
 * webcam light stays on for the whole attempt (browser policy), but
 * no preview is shown — the consent gate (F11 FE-1) already
 * communicated that the capture happens periodically.
 */
import * as React from "react";

import type { ProctorClient } from "@/lib/proctor-client";

export interface UseWebcamStillCaptureArgs {
  client: ProctorClient;
  /**
   * False (proctor profile=off OR runner not in_progress) → no-op.
   * Toggling false → true reopens the stream and resumes ticking.
   */
  enabled: boolean;
  /**
   * Capture cadence in milliseconds. Defaults to 30 s. Clamped to a
   * 5 s minimum so misconfigured callers don't accidentally DoS the
   * signed-URL pipeline.
   */
  intervalMs?: number;
}

export interface UseWebcamStillCaptureResult {
  capturesEnqueued: number;
  capturesUploaded: number;
  /** Last error surfaced by the loop. `null` while healthy. */
  lastError: WebcamStillCaptureError | null;
}

export type WebcamStillCaptureErrorKind =
  | "permission_denied"
  | "hardware_unavailable"
  | "transient_error"
  | "unsupported_browser"
  | "capture_readback_failed"
  | "upload_failed";

export interface WebcamStillCaptureError {
  kind: WebcamStillCaptureErrorKind;
  message: string;
}

const _MIN_INTERVAL_MS = 5_000;
const _DEFAULT_INTERVAL_MS = 30_000;
const _CAPTURE_WIDTH = 640;
const _CAPTURE_HEIGHT = 480;
const _JPEG_QUALITY = 0.7;

export function useWebcamStillCapture({
  client,
  enabled,
  intervalMs = _DEFAULT_INTERVAL_MS,
}: UseWebcamStillCaptureArgs): UseWebcamStillCaptureResult {
  const [capturesEnqueued, setCapturesEnqueued] = React.useState(0);
  const [capturesUploaded, setCapturesUploaded] = React.useState(0);
  const [lastError, setLastError] = React.useState<WebcamStillCaptureError | null>(null);

  // Refs survive React strict-mode double-mounts; the cleanup return
  // tears down the stream + interval so we don't leak GPU contexts.
  const streamRef = React.useRef<MediaStream | null>(null);
  const tickHandleRef = React.useRef<ReturnType<typeof setInterval> | null>(null);
  const cancelledRef = React.useRef(false);

  const cadence = Math.max(_MIN_INTERVAL_MS, intervalMs);

  React.useEffect(() => {
    if (!enabled) return;
    cancelledRef.current = false;

    const offscreen = document.createElement("canvas");
    offscreen.width = _CAPTURE_WIDTH;
    offscreen.height = _CAPTURE_HEIGHT;
    const ctx = offscreen.getContext("2d");
    if (!ctx) {
      setLastError({
        kind: "capture_readback_failed",
        message: "Browser refused 2D canvas context for proctor still capture.",
      });
      return;
    }

    // The offscreen <video> is detached from the DOM; we only need it
    // as a ``drawImage`` source. Detached <video> elements still play
    // when fed a MediaStream srcObject, which is enough for our needs.
    const video = document.createElement("video");
    video.muted = true;
    video.playsInline = true;
    video.autoplay = true;
    video.width = _CAPTURE_WIDTH;
    video.height = _CAPTURE_HEIGHT;

    async function captureOnce(): Promise<void> {
      const stream = streamRef.current;
      if (!stream || cancelledRef.current) return;
      // ``readyState >= HAVE_METADATA`` is required before ``drawImage``
      // produces a real frame. On the first tick the video may still be
      // priming; skip that frame and let the next tick fire.
      if (video.readyState < 2) return;
      try {
        if (!ctx) return;
        ctx.drawImage(video, 0, 0, _CAPTURE_WIDTH, _CAPTURE_HEIGHT);
      } catch (err) {
        setLastError({
          kind: "capture_readback_failed",
          message: errorMessage(err),
        });
        return;
      }
      const blob = await new Promise<Blob | null>((resolve) =>
        offscreen.toBlob((b) => resolve(b), "image/jpeg", _JPEG_QUALITY),
      );
      if (!blob || cancelledRef.current) return;
      setCapturesEnqueued((n) => n + 1);
      try {
        await client.uploadArtifact({
          blob,
          kind: "webcam_still",
          mimeType: "image/jpeg",
          capturedMetadata: {
            frame_width: _CAPTURE_WIDTH,
            frame_height: _CAPTURE_HEIGHT,
          },
        });
        if (!cancelledRef.current) {
          setCapturesUploaded((n) => n + 1);
          setLastError(null);
        }
      } catch (err) {
        if (cancelledRef.current) return;
        setLastError({
          kind: "upload_failed",
          message: errorMessage(err),
        });
      }
    }

    async function start(): Promise<void> {
      if (typeof navigator === "undefined" || !navigator.mediaDevices?.getUserMedia) {
        setLastError({
          kind: "unsupported_browser",
          message: "This browser does not support proctor webcam capture.",
        });
        return;
      }
      try {
        const stream = await navigator.mediaDevices.getUserMedia({
          video: {
            width: { ideal: _CAPTURE_WIDTH },
            height: { ideal: _CAPTURE_HEIGHT },
            facingMode: "user",
          },
          audio: false,
        });
        if (cancelledRef.current) {
          // Effect was torn down before the stream resolved.
          for (const t of stream.getTracks()) t.stop();
          return;
        }
        streamRef.current = stream;
        video.srcObject = stream;
        await video.play().catch(() => undefined);

        // First tick fires after the cadence delay so we don't catch
        // the priming frame; subsequent ticks at exactly the cadence.
        tickHandleRef.current = setInterval(() => {
          void captureOnce();
        }, cadence);

        // Surface stream-end (applicant revokes mid-attempt or pulls
        // the cable) as a `consent_denied` lifecycle event so the F12
        // review surface can render "stream ended at T+12m" markers.
        for (const track of stream.getVideoTracks()) {
          track.addEventListener("ended", () => {
            if (cancelledRef.current) return;
            void client.recordEvent("consent_denied", {
              reason: "webcam_loop_track_ended",
            });
            setLastError({
              kind: "hardware_unavailable",
              message: "Webcam track ended unexpectedly.",
            });
          });
        }
      } catch (err) {
        const kind = classifyMediaError(err);
        setLastError({ kind, message: errorMessage(err) });
        void client.recordEvent("consent_denied", {
          reason: `webcam_loop_${kind}`,
        });
      }
    }

    void start();

    return () => {
      cancelledRef.current = true;
      if (tickHandleRef.current !== null) {
        clearInterval(tickHandleRef.current);
        tickHandleRef.current = null;
      }
      const s = streamRef.current;
      streamRef.current = null;
      if (s) {
        for (const track of s.getTracks()) track.stop();
      }
      video.srcObject = null;
    };
  }, [enabled, cadence, client]);

  return { capturesEnqueued, capturesUploaded, lastError };
}

// ── Helpers ──────────────────────────────────────────────────────────────

function classifyMediaError(err: unknown): WebcamStillCaptureErrorKind {
  if (err === null || typeof err !== "object" || !("name" in err)) {
    return "transient_error";
  }
  const name = String((err as { name: unknown }).name);
  switch (name) {
    case "NotAllowedError":
    case "PermissionDeniedError":
    case "SecurityError":
      return "permission_denied";
    case "NotFoundError":
    case "DevicesNotFoundError":
    case "OverconstrainedError":
      return "hardware_unavailable";
    case "NotReadableError":
    case "TrackStartError":
    case "AbortError":
      return "transient_error";
    default:
      return "transient_error";
  }
}

function errorMessage(err: unknown): string {
  if (err === null || err === undefined) return "Unknown error.";
  if (typeof err === "string") return err;
  if (typeof err === "object" && "message" in err) {
    return String((err as { message: unknown }).message);
  }
  return "Unknown error.";
}
